"use client";
import {
  Image,
  Flex,
  Button,
  Icon,
  Modal,
  ModalBody,
  ModalHeader,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  Text,
  useToast,
  Spinner,
  Skeleton,
  Box,
  SkeletonText,
  Grid,
  Show,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import {
  useDeleteImage,
  useGetModelCreation,
  useGetImgGenModePrice,
  useGetModelById,
  useDeleteAllImageGenerations,
  useFalForUpscale,
  useToggleStar,
  useGetModelCreationStarSort
} from "queries";
import { useDisclosure } from "@chakra-ui/react";
import { FaTrash } from "react-icons/fa";
import { useAppContext } from "hooks";
import { WSS_IMG_GEN } from "api";
import { Jumbotron, PromptModal, NsfwImage, Navbar } from "components";
import { useImageSize } from "react-image-size";
import Link from "next/link";
import { SocialMediaAccount, Model } from "types";
import { LuInstagram, LuTwitter } from "react-icons/lu";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { Download, Trash2, Star } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query"
interface Creation {
  id: string;
  sas_url: string;
  file_name: string;
  upscaled: boolean;
  status: string;
  nsfw?: boolean | null;
  img2img?: boolean;
  is_starred?: boolean;
  storage_type?: string;
}

interface UpscaleFromImageRequest {
  file_name: string;
}


export const ModelCreations = ({ id }: { id: string }) => {
  const router = useRouter();
  const { data: generations, fetchNextPage, hasNextPage, refetch } = useGetModelCreationStarSort(8, id);
  const { data: model, isLoading } = useGetModelById(id!);
  // Add this below:
  const THIRTY_DAYS_IN_MS = 30 * 24 * 60 * 60 * 1000;

  const fluxExpireInMilliSecs = model?.isFlux && model.updated_at
  ? new Date(model.updated_at).getTime() + THIRTY_DAYS_IN_MS - Date.now()
  : 0;

  
  // Non-Flux expiration check
  const isNonFluxModelExpired = (model: Model): boolean => {
    if (model.isFlux) return false; // Not a Non-Flux model
    return !model.checkpoints?.[0]?.active; // Expired if `active` is not true
  };
  
  
  const { jwt, profile } = useAppContext();
  const [isDeleteAllOpen, setIsDeleteAllOpen] = useState(false);
  //@ts-ignore
  const totalImageGenerations = generations?.pages[0]?.total_image_generations ?? 0;
  const toast = useToast();
  const {
    isOpen: isInsufficientBalanceOpen,
    onOpen: onInsufficientBalanceOpen,
    onClose: onInsufficientBalanceClose,
  } = useDisclosure();

  const {
    isOpen: isImageWindowOpen,
    onOpen: onImageWindowOpen,
    onClose: onImageWindowClose,
  } = useDisclosure();

  const { isOpen: isDeleteOpen, onOpen: deleteOpen, onClose: deleteClose } = useDisclosure();
  const { data: imgGenModePrice } = useGetImgGenModePrice();
  const deleteImage = useDeleteImage();
  const [selectedImage, setSelectedImage] = useState<Creation | null>(null);
  const [upscalingImageFileName, setUpscalingImageFileName] = useState<string | null>(null);
  const [isInsufficientBalance, setIsInsufficientBalance] = useState<boolean>(false);
  const [isWebSocketProcessing, setIsWebSocketProcessing] = useState<boolean>(false);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [creditsRequired, setCreditsRequired] = useState<number>(1);
  const [dimensions] = useImageSize(selectedImage?.sas_url ?? "");
  const [selectedImageAspectRatio, setSelectedImageAspectRatio] = useState<number>(2 / 3);
  const [upscalingImageAspectRatio, setUpscalingImageAspectRatio] = useState<number>(2 / 3);
  const deleteAllImageGenerations = useDeleteAllImageGenerations(id);
  const { mutateAsync: upscaleFal } = useFalForUpscale(); 
  const [expireInMilliSecs, setExpireInMilliSecs] = useState<number>(0);
  const { isOpen: isPromptModalOpen, onOpen: openPromptModal, onClose: closePromptModal } = useDisclosure();

  const [selectedImages, setSelectedImages] = useState<string[]>([]);
  const [selectionMode, setSelectionMode] = useState(false);

  useEffect(() => {
    if (model?.checkpoints?.[0]?.expire_at) {
      setExpireInMilliSecs(
        new Date(model.checkpoints[0].expire_at).getTime() - Date.now()
      );
    }
  }, [model]);

  
  useEffect(() => {
    if (model?.checkpoints?.[0]?.expire_at) {
      const calculatedExpireInMilliSecs =
        new Date(model.checkpoints[0].expire_at).getTime() - Date.now();
      setExpireInMilliSecs(calculatedExpireInMilliSecs);
      console.log("Non-Flux expireInMilliSecs:", calculatedExpireInMilliSecs);
    }
  }, [model]);
  
  useEffect(() => {
    if (model?.isFlux && model.updated_at) {
      const calculatedFluxExpireInMilliSecs =
        new Date(model.updated_at).getTime() + THIRTY_DAYS_IN_MS - Date.now();
      console.log("Flux expireInMilliSecs:", calculatedFluxExpireInMilliSecs);
    }
  }, [model]);
  
  

  useEffect(() => {
    setCreditsRequired(imgGenModePrice?.upscale ?? 2);
  }, [imgGenModePrice]);

  useEffect(() => {
    if (dimensions) {
      setSelectedImageAspectRatio(dimensions.width / dimensions.height);
    } else {
      setSelectedImageAspectRatio(2 / 3);
    }
  }, [dimensions]);

  useEffect(() => {
    setIsInsufficientBalance(profile?.credit! - creditsRequired < 0);
  }, [profile, creditsRequired]);

  const handleDelete = () => {
    if (!selectedImage) return;

    deleteImage.mutate(
      { file_name: selectedImage.file_name },
      {
        onSuccess: () => {
          deleteClose();
          refetch();
        },
        onError: (error: any) => {
          console.error("Error deleting image:", error);
        },
      }
    );
  };

  const handleSelectImage = (creation: any) => {
    setSelectedImage(creation)
    openPromptModal()
  }
  useEffect(() => {
    console.log("Refetch triggered! New generations:", generations);
  }, [generations]);
  
  const queryClient = useQueryClient();

  const handleDownloadSelected = async () => {
    if (selectedImages.length === 0) {
        alert("No images selected for download.");
        return;
    }

    try {
        // 🚀 Cancel any ongoing fetches (to prevent race conditions)
        await queryClient.cancelQueries({ queryKey: ["image-generations"] });

        // 🏃‍♂️ Optimistically update UI state
        queryClient.setQueryData(["image-generations"], (oldData: any) => {
            if (!oldData) return oldData;
            return {
                ...oldData,
                pages: oldData.pages.map((page: any) => ({
                    ...page,
                    image_generations: page.image_generations.map((img: any) =>
                        selectedImages.includes(img.file_name) ? { ...img, isDownloading: true } : img
                    ),
                })),
            };
        });

        // ✅ Create a ZIP archive
        const zip = new JSZip();
        const folder = zip.folder("selected_images");

        await Promise.all(
            selectedImages.map(async (fileName) => {
                if (!fileName) return; // Skip invalid values

                try {
                    let blob;
                    let safeFileName = fileName.split("/").pop() || "image.jpg"; // Extract safe file name
                  
                    // ✅ If `file_name` is a direct URL (https://), use it directly
                    if (fileName.startsWith("https://")) {
                        console.log(`🌐 Fetching image directly from: ${fileName}`);

                        const response = await fetch(fileName, { mode: "cors" });

                        if (!response.ok) {
                            console.error(`❌ Failed to fetch ${fileName}:`, response.status);
                            return;
                        }

                        blob = await response.blob();
                    } else {
                        // ✅ Otherwise, find the correct image object
                        const selectedImage = generations?.pages
                            .flatMap((page) => page.image_generations)
                            .find(img => img.file_name === fileName);

                        if (!selectedImage) return;

                        // ✅ If Base64 (Non-Flux) → Convert to Blob
                        if (selectedImage.sas_url.startsWith("data:image")) {
                            console.log(`📸 Processing Base64 image: ${fileName}`);

                            const base64Data = selectedImage.sas_url.split(",")[1]; // Remove prefix
                            const byteCharacters = atob(base64Data);
                            const byteNumbers = new Array(byteCharacters.length);

                            for (let i = 0; i < byteCharacters.length; i++) {
                                byteNumbers[i] = byteCharacters.charCodeAt(i);
                            }

                            const byteArray = new Uint8Array(byteNumbers);
                            blob = new Blob([byteArray], { type: "image/jpeg" });

                        } else {
                            // ✅ Otherwise, fetch the image from `sas_url`
                            console.log(`🌐 Fetching image from: ${selectedImage.sas_url}`);

                            const response = await fetch(selectedImage.sas_url, { mode: "cors" });

                            if (!response.ok) {
                                console.error(`❌ Failed to fetch ${fileName}:`, response.status);
                                return;
                            }

                            blob = await response.blob();
                        }
                    }

                    // ✅ Ensure valid image format
                    if (blob.type !== "image/jpeg" && blob.type !== "image/png") {
                        console.error("❌ Invalid image format:", blob.type);
                        return;
                    }

                    // ✅ Ensure the filename has the correct extension
                    if (!safeFileName.endsWith(".jpeg") && !safeFileName.endsWith(".jpg") && !safeFileName.endsWith(".png")) {
                        safeFileName += ".jpeg";
                    }

                    // ✅ Add file to ZIP
                    folder?.file(safeFileName, blob);
                } catch (error) {
                    console.error("❌ Error processing:", fileName, error);
                }
            })
        );

        // ✅ Generate ZIP and trigger download (only if ZIP is not empty)
        if (Object.keys(folder?.files || {}).length > 0) {
            const zipBlob = await zip.generateAsync({ type: "blob" });
            saveAs(zipBlob, "selected_images.zip");
        } else {
            console.warn("❌ No valid images found to download.");
        }

        // ✅ Invalidate queries and refetch fresh data
        queryClient.invalidateQueries({ queryKey: ["image-generations"] });
        refetch();
    } catch (error) {
        console.error("❌ Failed to download images:", error);
    }
};


  

const handleSelectMany = () => {
  if (!selectionMode) {
    // Flatten and filter valid image generations
    const recentImages = generations?.pages
      ?.flatMap((page) => {
        if (typeof page !== "object" || page === null) return []; // Ensure it's an object
        return (page as any).image_generations || []; // Type assertion
      })
      .filter((creation: any) => creation.status !== "pending" && creation.status !== "failed")
      .slice(0, 8) // Get the most recent 8
      .map((creation: any) => creation.file_name) || []; // ✅ Ensure an empty array if undefined

    setSelectedImages(recentImages); // ✅ recentImages is guaranteed to be an array
  } else {
    setSelectedImages([]); // Clear selection if cancelling
  }

  setSelectionMode(!selectionMode);
};



const handleDeleteSelected = async () => {
  if (selectedImages.length === 0) return;

  // Confirm before deleting
  const confirmDelete = window.confirm(`Are you sure you want to delete ${selectedImages.length} selected images?`);
  if (!confirmDelete) return;

  // Loop through selected images and delete each
  await Promise.all(selectedImages.map(async (fileName) => {
    try {
      await deleteImage.mutateAsync({ file_name: fileName });
    } catch (error) {
      console.error(`Error deleting image: ${fileName}`, error);
    }
  }));

  // Clear the selection after deletion
  setSelectedImages([]);

  // Refetch to update UI
  refetch();
};

  
  
const handleDeleteAll = async () => {
  // Confirm before deleting all images
  const confirmDelete = window.confirm(
    `Are you sure you want to delete all ${totalImageGenerations} images? This action cannot be undone.`
  );
  if (!confirmDelete) return;

  try {
    // Flatten all pages to get all image filenames
    const allImageFilenames = generations?.pages
      ?.flatMap((page) => page.image_generations)
      ?.map((creation) => creation.file_name) || [];

    if (allImageFilenames.length === 0) {
      toast({
        title: "No Images Found",
        description: "There are no images to delete.",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // 🚀 Delete all images in parallel
    await Promise.all(
      allImageFilenames.map(async (fileName) => {
        try {
          await deleteImage.mutateAsync({ file_name: fileName });
        } catch (error) {
          console.error(`❌ Error deleting image: ${fileName}`, error);
        }
      })
    );

    toast({
      title: "Success",
      description: "All images have been deleted successfully.",
      status: "success",
      duration: 5000,
      isClosable: true,
    });

    // Refetch the data to update UI
    refetch();
  } catch (error) {
    console.error("❌ Error deleting all images:", error);
    toast({
      title: "Error",
      description: `Failed to delete all images: ${
        error instanceof Error ? error.message : "Unknown error"
      }`,
      status: "error",
      duration: 5000,
      isClosable: true,
    });
  }
};
  

  return (
    <Flex bg="#050918" direction="column">
      {/* Mobile, < 768px */}
      <Show below="md-1">
        <Navbar />
        <Flex px={6} align="center" direction="column">
          {isLoading ? (
            <Skeleton mt="50px" h="200px" w="200px" />
          ) : (
            <Image
              mt="50px"
              h="200px"
              w="200px"
              borderRadius="15px"
              // @ts-ignore
              src={model?.picture_url || "default_image_url"}
              objectFit="contain"
            />
          )}
          {isLoading ? (
            <SkeletonText mt="25px" noOfLines={1} fontSize="56px" w="300px" />
          ) : (
            <Text
              mt="25px"
              style={{
                background: "linear-gradient(to right,  #FFE1FF 10%, #A4DBEE 50%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                MozBackgroundClip: "text",
              }}
              fontSize="28px"
              fontWeight="extrabold"
            >
                                    {`${model?.display_name}`}

            </Text>
          )}
          <Flex>
            {model?.social_media_accounts?.map((social: SocialMediaAccount, index: number) => (
              <Box w="20px" key={index}>
                <Link target="_blank" href={social.link}>
                  {social.social_media.name === "twitter" ? (
                    <LuTwitter style={{ marginRight: "10px" }} size={20} />
                  ) : (
                    <LuInstagram size={20} />
                  )}
                </Link>
              </Box>
            ))}
          </Flex>
          {isLoading ? (
            <SkeletonText mb="40px" mt="25px" noOfLines={4} spacing="4" />
          ) : (
            <Text mb="40px" mt="25px" color="#7E95AA" fontSize="13px">
              {model?.description}
            </Text>
          )}

          {!location.pathname.includes("create") && (
            <Button
              p={6}
              borderRadius="16px"
              color="white"
              bg="#FF1B6D"
              _hover={{
                bg:  model &&
                ((model.isFlux && fluxExpireInMilliSecs <= 0) ||
                (!model.isFlux && isNonFluxModelExpired(model))) ? "gray.400" : "#FF316D",
                cursor:
                model &&
                ((model.isFlux && fluxExpireInMilliSecs <= 0) ||
                (!model.isFlux && isNonFluxModelExpired(model)))
                  
                  ? "not-allowed"
                  : "pointer",
            }}
              onClick={() => {
                if (
                  model &&
                  ((model.isFlux && fluxExpireInMilliSecs <= 0) || isNonFluxModelExpired(model))
                ) {
                  return; // Prevent navigation for expired models
                }
                router.push(
                  model?.user_id && model?.isFlux
                    ? `/mymodels/${model?.username}/create-flux` // User-owned Flux model
                    : model?.isFlux
                    ? `/public-models/${model?.username}/create-flux` // Public Flux model
                    : model?.user_id
                    ? `/mymodels/${model?.username}/create` // User-owned Non-Flux model
                    : `/public-models/${model?.username}/create` // Public Non-Flux model
                )
              }}
            >
              <Text fontSize="16px" fontWeight="bold">
                Play with this model
              </Text>
            </Button>
          )}
        </Flex>
      </Show>
      {/* Desktop, >= 768px */}
      <Show above="md">
        <Jumbotron background={"linear-gradient(to bottom, #16181F, #121C3D)"}>
          <Flex w="100%" h="350px" align="center">
            <Flex justify="space-between" w="100%" align="end">
              <Flex align="end">
                {isLoading ? (
                  
                  <Skeleton h="200px" w="200px" />
                ) : (
                  <Image
                    h="200px"
                    w="200px"
                    // @ts-ignore
                    src={model?.picture_url || "default_image_url"}
                    objectFit="contain"
                    borderRadius="12px"
                  />
                )}
                <Flex justify="space-between" zIndex={2} ml="50px" direction="column">
                  {isLoading ? (
                    <SkeletonText noOfLines={1} fontSize="56px" w="300px" />
                  ) : (
                    <Text
                      style={{
                        background: "linear-gradient(to right,  #FFE1FF 10%, #A4DBEE 50%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        MozBackgroundClip: "text",
                      }}
                      fontSize="56px"
                      fontWeight="extrabold"
                    >
                                            {`${model?.display_name}`}

                    </Text>
                  )}

                  <Flex>
                    {isLoading ? (
                      <Skeleton h="20px" w="20px" mb="4" />
                    ) : (
                      model?.social_media_accounts?.map(
                        (social: SocialMediaAccount, index: number) => (
                          <Box w="20px" key={index}>
                            <Link target="_blank" href={social.link}>
                              {social.social_media.name === "twitter" ? (
                                <LuTwitter />
                              ) : (
                                <LuInstagram />
                              )}
                            </Link>
                          </Box>
                        )
                      )
                    )}
                  </Flex>

                  {isLoading ? (
                    <SkeletonText mt="4" noOfLines={4} spacing="4" />
                  ) : (
                    <Text maxW="550px" color="#7E95AA" fontSize="13px" w="650px" mt="20px">
                      {model?.description}
                    </Text>
                  )}
                </Flex>
              </Flex>

              <Button
                mt="25px"
                p={6}
                borderRadius="16px"
                color="white"
                bg="#FF1B6D"
                _hover={{
                  bg:
                    model &&
                    model.user_id === null // If public model
                      ? "#FF316D" // Always allow hover for public models
                      : model &&
                        ((model.isFlux && fluxExpireInMilliSecs <= 0) ||
                          (!model.isFlux && isNonFluxModelExpired(model)))
                      ? "gray.400"
                      : "#FF316D",
                  cursor:
                    model &&
                    model.user_id === null // If public model
                      ? "pointer" // Always allow pointer for public models
                      : model &&
                        ((model.isFlux && fluxExpireInMilliSecs <= 0) ||
                          (!model.isFlux && isNonFluxModelExpired(model)))
                      ? "not-allowed"
                      : "pointer",
                }}
                onClick={() => {
                  
                  if (
                    model &&
                    model.user_id === null // Always allow public models
                  ) {
                    router.push(
                      model.isFlux
                        ? `/public-models/${model.username}/create-flux` // Public Flux model
                        : `/public-models/${model.username}/create` // Public Non-Flux model
                    );
                    return;
                  }
                  if (
                    
                    model &&
                    ((model.isFlux && fluxExpireInMilliSecs <= 0) ||
                    (!model.isFlux && isNonFluxModelExpired(model)))
                  ) {
                    console.log("Button disabled: Model is expired.");
                    return; // Prevent navigation for expired models
                  }
                  router.push(
                    model?.user_id && model?.isFlux
                      ? `/mymodels/${model?.username}/create-flux` // User-owned Flux model
                      : model?.isFlux
                      ? `/public-models/${model?.username}/create-flux` // Public Flux model
                      : model?.user_id
                      ? `/mymodels/${model?.username}/create` // User-owned Non-Flux model
                      : `/public-models/${model?.username}/create` // Public Non-Flux model
                  )
                }}               
              >
                <Text fontSize="14px" fontWeight="bold">
                  Play with this model
                </Text>
              </Button>
            </Flex>
          </Flex>
        </Jumbotron>
      </Show>
      <Flex 
  px={{
    base: "10px",
    sm: "20px",
    md: "30px",
    lg: "40px",
    xl: "50px",
    "2xl": "15%"
  }} 
  align="center" 
  direction="column"
>     
  <Modal size="xl" isOpen={isDeleteAllOpen} onClose={() => setIsDeleteAllOpen(false)}>
        <ModalOverlay />
        <ModalContent borderRadius={0} bg="#16181F" border="1px solid white">
          <ModalCloseButton />
          <Flex
            justify="center"
            w="100%"
            p={10}
            px={{ base: "30px", md: "80px" }}
            minH="300px"
            direction="column"
          >
            <Text fontSize={{ base: "28px", md: "36px" }} fontWeight={600}>
              Are you sure you want to delete all {totalImageGenerations} images?
            </Text>
            <Text mt={4} fontSize={{ base: "16px", md: "18px" }} color="red.300">
              This action cannot be undone.
            </Text>
            <Flex mt={10} justifyContent="space-between">
              <Button 
                bg="gray.500" 
                color="white" 
                onClick={() => setIsDeleteAllOpen(false)}
              >
                Cancel
              </Button>
              <Button 
                bg="red.500" 
                color="white" 
                onClick={handleDeleteAll}
                
              >
                Delete All Images
              </Button>
            </Flex>
          </Flex>
        </ModalContent>
      </Modal>
      <Text
    fontSize={{ base: "32px", sm: "38px", lg: "58px" }}
    fontWeight="semibold"
    textAlign={{ base: "center", md: "left" }}
    flex={{ base: "1", md: "0 1 auto" }}
    mx={{ base: "auto", md: "0" }}
    mt="10px"
    sx={{
      background: "linear-gradient(to right, #FFE1FF 40%, #A4DBEE 60%)",
      WebkitBackgroundClip: "text",
      WebkitTextFillColor: "transparent",
      backgroundClip: "text",
      textFillColor: "transparent",
    }}
  >
    Creations
  </Text>
  
  <Box width={{ base: "0px", lg: "100px" }} /> {/* Spacer to balance layout */}
{/* Top Section with Title and Buttons */}
<Flex 
  width="100%" 
  justifyContent="space-between" 
  alignItems="center" 
  mt="30px" 
  px={{ base: "10px", md: "30px" }}  
  flexWrap="wrap"// Reduced padding for better alignment
>

  {/* Left Section - Sticking More to Left */}
  <Flex gap="10px" justifyContent="flex-start"  flexWrap="wrap" alignItems="center"  >  

    <Flex alignItems="center" gap="10px">
    <Button
      color="white"
      bg={selectionMode && selectedImages.length > 0 ? "transparent" : "gray.600"} 
      borderRadius="12px"
      border={selectionMode ? "2px solid white" : "none"}  
      px="12px"
      py="8px"
      fontSize="16px"
      fontWeight="medium"
      display="flex"
      alignItems="center"
      gap="6px"
      _hover={{ bg: "gray.200" }}
      onClick={handleSelectMany}
    >
      {selectedImages.length > 0 ? `${selectedImages.length} selected` : "Select Many"}
    </Button>
    {/* Show "Clear Selection" only when images are selected */}
  {selectedImages.length > 0 && (
    <Button
      color="white"
      bg="gray.600"
      borderRadius="12px"
      px="12px"
      py="8px"
      fontSize="16px"
      fontWeight="medium"
      display="flex"
      alignItems="center"
      gap="6px"
      _hover={{ bg: "gray.500" }}
      onClick={() => {
        setSelectedImages([]); // Clear all selections
        setSelectionMode(false); // Exit selection mode
      }} // Clear all selections
    >
      × Clear 
    </Button>
  )}
  </Flex>
  </Flex>

  {/* Right Section - Push Further Right */}
  <Flex gap="10px" ml="auto"   mt={{ base: "10px", md: "0" }} // ✅ Add margin-top on mobile screens
    width={{ base: "100%", md: "auto" }} justifyContent={{ base: "flex-wrap", md: "flex-end" }}>  
    {selectedImages.length === 0 && !selectionMode && (
  <Button
    color="white"
    bg="#742a2a" // Matches the dark red background
    borderRadius="12px"
    px="12px"
    py="8px"
    fontSize="16px"
    fontWeight="medium"
    display="flex"
    alignItems="center"
    gap="6px"
    _hover={{ bg: "red.600" }} // Slightly lighter red on hover
    onClick={handleDeleteAll}
  >
    <Icon as={Trash2} boxSize={5} /> 
    Delete All
  </Button>
)}

    {selectedImages.length > 0 && (
      <>
      <Button
        color="black"
        bg="white"
        borderRadius="12px"
        px="12px"
        py="8px"
        fontSize="16px"
        fontWeight="medium"
        display="flex"
        alignItems="center"
        gap="6px"
        _hover={{ bg: "gray.200" }}
        onClick={handleDownloadSelected}
      >
        <Icon as={Download} boxSize={5} />
        Download
      </Button>
      <Button
        color="white"
        bg="#742a2a" // Matches the dark red background
        borderRadius="12px"
        px="12px"
        py="8px"
        fontSize="16px"
        fontWeight="medium"
        display="flex"
        alignItems="center"
        gap="6px"
        _hover={{ bg: "red.600" }} // Slightly lighter red on hover
        onClick={handleDeleteSelected}
      >
        <Icon as={Trash2} boxSize={5} /> 
        Delete
      </Button>
      </>
    )}
  </Flex>

</Flex>


        <Grid
          mt="25px"
          gap={2}
          templateColumns={{
            base: "repeat(2,1fr)",
            md: "repeat(3,1fr)",
            lg: "repeat(4,1fr)",
          }}
        >
          {" "}
          {selectedImage && <PromptModal 
            isOpen={isPromptModalOpen} 
            onClose={closePromptModal} 
            isFlux={model?.isFlux} 
            model_id={id} 
            generationImage={selectedImage?.file_name.startsWith("http") ? selectedImage?.file_name : selectedImage?.sas_url} 
            file_name={selectedImage?.file_name} 
            storage_type={selectedImage?.storage_type}
          />}
          {generations?.pages.flatMap((page) =>
          //@ts-ignore
          page?.image_generations
          ?.filter((creation: any) => creation.status !== "pending" && creation.status !== "failed")
          ?.map((creation: any) => (
            <CreationCard
              key={creation.file_name}
              creation={creation}
              deleteOpen={() => deleteOpen()}
              setSelectedImage={handleSelectImage}
              selectedImages={selectedImages}  // Pass state
              setSelectedImages={setSelectedImages}  // Pass state setter
              refetch={refetch}
              id={id} 
            />
          ))
        )}
        </Grid>
        {hasNextPage && (
          <Button
            onClick={() => fetchNextPage()}
            color="white"
            mt="35px"
            borderRadius="16px"
            bg="#FF1B6D"
            _hover={{ opacity: 0.9, bg: "#FF1B6D" }}
          >
            Load More
          </Button>
        )}
      </Flex>


      <Modal size="xl" isOpen={isDeleteOpen} onClose={deleteClose}>
        <ModalOverlay />
        <ModalContent borderRadius={0} bg="#16181F" border="1px solid white">
          <ModalCloseButton />
          <Flex
            justify="center"
            w="100%"
            p={10}
            px={{ base: "30px", md: "80px" }}
            minH="300px"
            direction="column"
          >
            <Text fontSize={{ base: "28px", md: "36px" }} fontWeight={600}>
              Are you sure you want to delete this image?
            </Text>
            <Flex>
              <Button mt="55px" bg="white" borderRadius="32px" color="black" onClick={handleDelete}>
                Delete Image
              </Button>
            </Flex>
          </Flex>
        </ModalContent>
      </Modal>
      <InsufficientCreditsModal
        isOpen={isInsufficientBalanceOpen}
        onClose={onInsufficientBalanceClose}
      />
      <Box h="150px" />
    </Flex>
  );
};
const CreationCard = ({
  creation,
  setSelectedImage,
  deleteOpen,
  selectedImages,
  setSelectedImages,
  refetch,
  id,
}: {
  creation: Creation;
  setSelectedImage: (creation: Creation | null) => void;
  deleteOpen: () => void;
  selectedImages: string[];
  setSelectedImages: (images: string[]) => void;
  refetch: () => void; 
  id: string; 

}) => {
  const { profile } = useAppContext();
  const { mutate: toggleStar } = useToggleStar(); // Use the hook
  const toast = useToast();
  // Determine the image source, prioritizing `sas_url` if available, otherwise using `file_name`

  // Handlers for better state management
  const handleImageClick = () => {
    setSelectedImage(creation);
  };
  

  const [isFavorited, setIsFavorited] = useState(creation.is_starred ?? false);
  const queryClient = useQueryClient();

  const handleStarClick = async () => {
    setIsFavorited((prev) => !prev); // 🔥 Optimistic UI update
  
    try {
      await queryClient.cancelQueries({ queryKey: ["generation", id] });
  
      queryClient.setQueryData(["generation", id], (oldData: any) => {
        if (!oldData) return oldData;
  
        // Flatten all pages into a single array
        let allImages = oldData.pages.flatMap((page: any) => page.image_generations);
  
        // Find and update the starred image
        const updatedImages = allImages.map((img: any) =>
          img.id === creation.id ? { ...img, is_starred: !isFavorited } : img
        );
  
        // Sort so that starred images move to the top
        updatedImages.sort((a: any, b: any) => {
          if (b.is_starred === a.is_starred) return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
          return b.is_starred ? -1 : 1; // Move starred images to the top
        });
  
        // Reconstruct paginated structure (assuming 8 images per page)
        const newPages = [];
        for (let i = 0; i < updatedImages.length; i += 8) {
          newPages.push({ image_generations: updatedImages.slice(i, i + 8) });
        }
  
        return { ...oldData, pages: newPages };
      });
  
      // 🔄 Call the mutation to persist the change on the backend
      await toggleStar({ imageId: creation.id, isStarred: !isFavorited });
  
      queryClient.invalidateQueries({ queryKey: ["generation", id] }); // Ensure fresh data after mutation
      refetch(); // Fetch fresh data
    } catch (error) {
      console.error("❌ Failed to update favorite status:", error);
      setIsFavorited((prev) => !prev); // ❌ Revert UI if mutation fails
    }
  };
  
  
   
    // Toggle selection of images
    const toggleImageSelection = () => {
      let updatedSelection = [...selectedImages]; // Copy current state
    
      if (updatedSelection.includes(creation.file_name)) {
        updatedSelection = updatedSelection.filter((img) => img !== creation.file_name); // Remove
      } else {
        if (updatedSelection.length >= 8) {
          toast({
            title: "Selection Limit Reached",
            description: "You can select up to 8 images only.",
            status: "error",
            duration: 3000,
            isClosable: true,
          });
          return; // Stop adding more selections
        }
        updatedSelection.push(creation.file_name); // Add
      }
    
      setSelectedImages(updatedSelection); // Update state
    };
    
  
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedImage(creation);
    deleteOpen();
  };
  const imageSrc = creation.file_name.startsWith("http") ? creation.file_name : creation.sas_url;

  const [isHovered, setIsHovered] = useState(false);

  return (
    <Flex key={imageSrc} direction="column" position="relative" >
      <Flex position="relative" direction="column" justifyContent="center" alignItems="center">
      <Box position="absolute" top="8px" left="8px" zIndex="2">
  <input
    type="checkbox"
    checked={selectedImages.includes(creation.file_name)}
    onChange={toggleImageSelection}
    style={{
      width: "20px",
      height: "20px",
      cursor: "pointer",
      borderRadius: "4px",
      appearance: "none", // Removes default browser styles
      backgroundColor: selectedImages.includes(creation.file_name) ? "#fff" : "rgba(3, 3, 3, 0.2)", // White when checked
      border: "2px solid #888",
      transition: "background 0.2s ease-in-out",
      position: "relative",
    }}
  />
  <style>
    {`
      input[type="checkbox"]:checked::before {
        content: "✔";
        font-size: 14px;
        font-weight: bold;
        color: #000; /* Black tick */
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
      }
         input[type="checkbox"]:hover {
        background: rgba(255, 255, 255, 0.3); /* Slightly more visible on hover */
      }
    `}
  </style>
</Box>

  {/* Star Button with Correct Hover & Selection Effect */}
      <Box
        position="absolute"
        top="8px"
        right="8px"
        zIndex="2"
        display="flex"
        alignItems="center"
        justifyContent="center"
        w="26px"
        h="26px"
        borderRadius="8px"
        transition="background 0.2s ease-in-out"
        bg={isHovered ? "rgba(0, 0, 0, 0.4)" : "transparent"} // Black-transparent only on hover
        _hover={{ cursor: "pointer" }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={handleStarClick}
      >
        <Icon
          as={Star}
          boxSize={5}
          color={isFavorited || isHovered ? "#FACC15" : "white"} // Yellow if favorited or hovered, white otherwise
          fill={isFavorited ? "#FACC15" : "transparent"} // Filled yellow if favorited, transparent otherwise
          stroke={isFavorited ? (isHovered ? "white" : "#FACC15") : isHovered ? "#FACC15" : "white"} // White outline when favorited & hovered, yellow on hover if not favorited, otherwise white
          strokeWidth="2px" // Always have a stroke for outline
          transition="color 0.2s ease-in-out, fill 0.2s ease-in-out, stroke 0.2s ease-in-out"
        />


      </Box>


        <Box
          cursor="pointer"
          onClick={() => setSelectedImage(creation)}
          borderRadius="14px"
          overflow="hidden"
          w="100%"
          sx={{ aspectRatio: "3/4" }}
        >
          <NsfwImage
            src={imageSrc}
            alt="Generated image"
            nsfw={creation.nsfw && !profile?.nsfw_consent}
          />
        </Box>

        {creation.upscaled && (
          <Flex
            position="absolute"
            top={{ base: "2", md: "5" }}
            right={{ base: "2", md: "5" }}
            bgColor="black"
            w="24px"
            h="16px"
            justifyContent="center"
            alignItems="center"
            border="1px solid yellow"
            borderRadius="2px"
          >
            <Text fontSize="12px" fontWeight="bold" color="yellow" textAlign="center">
              HD
            </Text>
          </Flex>
        )}

        {/* <Button
          variant="unstyled"
          bg="red"
          w="32px"
          h="32px"
          position="absolute"
          bottom={{ base: "2", md: "5" }}
          right={{ base: "2", md: "5" }}
          onClick={handleDeleteClick}
        >
          <Icon as={FaTrash} boxSize={4} />
        </Button> */}
      </Flex>
    </Flex>
  );
};



// Define the types for the component's props
type InsufficientCreditsModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const InsufficientCreditsModal: React.FC<InsufficientCreditsModalProps> = ({
  isOpen,
  onClose,
}) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader color="black">Insufficient Credits</ModalHeader>
        <ModalCloseButton />
        <ModalBody color="black">
          <p>You do not have enough credits to continue.</p>
          <Button colorScheme="blue" onClick={onClose}>
            Close
          </Button>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ModelCreations;